import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Edit } from 'qampo-ui-library';
import TrashIcon from '../../assets/icons/trash.svg?react';
import { Table } from '../UI';
import { Button } from '../UI/base/Button';
import type { TableColumn } from '../UI';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { StaffCompetenceFormModal } from './StaffCompetenceFormModal';
import type { StaffCompetenceFormValues } from './StaffCompetenceFormModal';
import { DeleteStaffCompetenceModal } from './DeleteStaffCompetenceModal';
import { useStaffCompetences } from '../../hooks/useStaffCompetences';
import type { StaffProfessionResponse } from '../../api/generated';
import { useAppDispatch } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { getApiErrorMessage } from '../../utils/errorMessageUtils';
import styles from './StaffCompetencesSettings.module.css';

export const StaffCompetencesSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    competences,
    isLoading,
    loadError,
    createCompetence,
    updateCompetence,
    deleteCompetence
  } = useStaffCompetences();

  const [formOpen, setFormOpen] = useState(false);
  const [editingCompetence, setEditingCompetence] = useState<StaffProfessionResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StaffProfessionResponse | null>(null);

  const columns = useMemo<TableColumn<StaffProfessionResponse>[]>(
    () => [
      {
        key: 'name',
        header: t('config-sc-col-name'),
        render: (c) => <span className={styles.nameCell}>{c.name}</span>
      },
      {
        key: 'showInKpi',
        header: t('config-sc-col-show-in-kpi'),
        width: 160,
        render: (c) => (
          <span className={`${styles.badge} ${c.showInKpi ? styles.badgeOn : styles.badgeOff}`}>
            {c.showInKpi ? t('config-sc-shown') : t('config-sc-hidden')}
          </span>
        )
      },
      {
        key: 'actions',
        header: '',
        width: 100,
        headerAlign: 'center',
        render: (c) => (
          <div className={styles.rowActions}>
            <IconButton
              size="small"
              icon={<Edit width={14} height={14} />}
              onClick={() => {
                setEditingCompetence(c);
                setFormOpen(true);
              }}
              classes={styles.actionBtn}
            />
            <IconButton
              size="small"
              icon={<TrashIcon width={14} height={14} />}
              onClick={() => setDeleteTarget(c)}
              classes={`${styles.actionBtn} ${styles.actionBtnDelete}`}
            />
          </div>
        )
      }
    ],
    [t]
  );

  const handleOpenCreate = () => {
    setEditingCompetence(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingCompetence(null);
  };

  const handleSave = async (values: StaffCompetenceFormValues) => {
    const target = editingCompetence;
    try {
      if (target) {
        await updateCompetence(target.id, values);
      } else {
        await createCompetence(values);
      }
      handleCloseForm();
    } catch (e) {
      console.error('Failed to save staff competence:', e);
      const fallback = target ? t('config-sc-update-error') : t('config-sc-create-error');
      dispatch(showError({ message: getApiErrorMessage(e, fallback) }));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const idToDelete = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await deleteCompetence(idToDelete);
    } catch (e) {
      console.error('Failed to delete staff competence:', e);
      dispatch(showError({ message: getApiErrorMessage(e, t('config-sc-delete-error')) }));
    }
  };

  const showEmptyState = !isLoading && !loadError && competences.length === 0;

  return (
    <section>
      <SectionHeader title={t('config-tab-staff-competences')}>
        <Button type="primary" onClick={handleOpenCreate}>
          {t('config-sc-add')}
        </Button>
      </SectionHeader>

      {isLoading && competences.length === 0 && (
        <p className={styles.emptyState}>{t('config-sc-loading')}</p>
      )}

      {loadError && <p className={styles.emptyState}>{t('config-sc-load-error')}</p>}

      {showEmptyState && <p className={styles.emptyState}>{t('config-sc-empty')}</p>}

      {competences.length > 0 && (
        <Table
          columns={columns}
          data={competences}
          getRowKey={(c) => c.id}
          minWidth={560}
        />
      )}

      {formOpen && (
        <StaffCompetenceFormModal
          competence={editingCompetence}
          onSave={(v) => void handleSave(v)}
          onClose={handleCloseForm}
        />
      )}

      {deleteTarget && (
        <DeleteStaffCompetenceModal
          competenceName={deleteTarget.name}
          onConfirm={() => void handleConfirmDelete()}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </section>
  );
};
